const hasOwnProperty = (value: object, property: string): boolean => Object.prototype.hasOwnProperty.call(value, property);

export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	value !== null && typeof value === "object" && !Array.isArray(value);

export const isValidScribePackSelectionDraft = (value: unknown): boolean => {
	if (!isPlainObject(value)) {
		return false;
	}
	return (
		(!hasOwnProperty(value, "pack") || value.pack === null || isPlainObject(value.pack)) &&
		(!hasOwnProperty(value, "taskIds") || value.taskIds === null || Array.isArray(value.taskIds)) &&
		(!hasOwnProperty(value, "userInputData") || value.userInputData === null || isPlainObject(value.userInputData))
	);
};

export const normalizeScribePackSelectionDraft = (value: Record<string, unknown>) => ({
	pack: hasOwnProperty(value, "pack") ? value.pack : null,
	taskIds: hasOwnProperty(value, "taskIds") ? value.taskIds : null,
	userInputData: hasOwnProperty(value, "userInputData") ? value.userInputData : null
});

interface InstructionCreateReturnOptions {
	defaultDraftKey: string;
	getDraftKey: () => string | null;
	// Per-surface noun for the warning wording (e.g. "Start Recording", "Take Photos") so a future
	// Datadog warning still names the source. Defaults to a generic label.
	label?: string;
}

interface InstructionReturnRoute {
	query?: Record<string, unknown>;
}

interface InstructionReturnRouter {
	replace: (location: { query: Record<string, unknown> }) => unknown;
}

interface InstructionReturnContext {
	action: string;
	instructionId: string | null;
	draftKey: string | null;
}

const readValidReturnString = (value: unknown): string | null => (typeof value === "string" && value.length > 0 ? value : null);

export const hasInstructionCreateReturnQuery = (route: InstructionReturnRoute): boolean =>
	["action", "instructionId", "draftKey"].some((key) => hasOwnProperty(route.query ?? {}, key));

export function consumeInstructionCreateReturnQuery(
	route: InstructionReturnRoute,
	router: InstructionReturnRouter,
	allowedActions: string | string[]
): InstructionReturnContext | null {
	const query = route.query ?? {};
	if (!hasInstructionCreateReturnQuery(route)) {
		return null;
	}

	const { action, instructionId, draftKey, ...rest } = Object.fromEntries(Object.entries(query));
	router.replace({ query: rest });

	const returnedAction = readValidReturnString(action);
	const returnedInstructionId = readValidReturnString(instructionId);
	const returnedDraftKey = readValidReturnString(draftKey);
	const acceptedActions = Array.isArray(allowedActions) ? allowedActions : [allowedActions];
	if (!returnedAction || !acceptedActions.includes(returnedAction) || (!returnedInstructionId && !returnedDraftKey)) {
		console.warn(
			"openReturnedStartRecordingDialog: return query rejected (action not in the handoff allowlist or " +
				"missing instructionId/draftKey); discarding handoff.",
			{ returnedAction, hasInstructionId: !!returnedInstructionId, hasDraftKey: !!returnedDraftKey }
		);
		return null;
	}

	return { action: returnedAction, instructionId: returnedInstructionId, draftKey: returnedDraftKey };
}

export function useInstructionCreateReturn(options: InstructionCreateReturnOptions) {
	const label = options.label ?? "action dialog";
	let returnContext: InstructionReturnContext | null = null;
	let returnQueryConsumed = false;

	function consumeReturnQuery(
		route: InstructionReturnRoute,
		router: InstructionReturnRouter,
		allowedActions: string | string[]
	): InstructionReturnContext | null {
		if (returnQueryConsumed || !hasInstructionCreateReturnQuery(route)) {
			return null;
		}
		returnQueryConsumed = true;
		returnContext = consumeInstructionCreateReturnQuery(route, router, allowedActions);
		const owningDraftKey = options.getDraftKey() || options.defaultDraftKey;
		if (returnContext && returnContext.draftKey !== owningDraftKey) {
			console.warn(
				"openReturnedStartRecordingDialog: return query rejected (standalone handoff missing or mismatched draftKey); preserving owning draft.",
				{ returnedAction: returnContext.action, hasInstructionId: !!returnContext.instructionId, hasDraftKey: !!returnContext.draftKey }
			);
			returnContext = null;
		}
		return returnContext;
	}

	// The sessionStorage slot for the in-progress draft. sessionStorage (not local) because the
	// detour is same-tab and short-lived and must not leak across browser tabs. A caller-supplied
	// key (threaded back on return) wins over the surface default so restore reads the same slot
	// the persist wrote.
	function storageKey(): string {
		return returnContext?.draftKey || options.getDraftKey() || options.defaultDraftKey;
	}

	function returnedInstructionId(fallback: string | null): string | null {
		return returnContext ? returnContext.instructionId : fallback;
	}

	// Persist the in-progress dialog fields before the same-tab navigation to the editor. The caller
	// passes only the minimal serializable shape (the same fields its submit reads) so a
	// non-serializable selection cannot break JSON.stringify.
	function persistDraft(draft: Record<string, unknown>): void {
		try {
			window.sessionStorage.setItem(storageKey(), JSON.stringify(draft));
		} catch (error) {
			console.warn(`Unable to persist the ${label} draft before instruction create`, error);
		}
	}

	function loadRawDraft(): Record<string, unknown> | null {
		const key = storageKey();
		let raw: string | null = null;
		try {
			raw = window.sessionStorage.getItem(key);
		} catch (error) {
			console.warn(`Unable to read the persisted ${label} draft`, error);
		}
		if (!raw) {
			return null;
		}
		try {
			const parsed = JSON.parse(raw);
			return isPlainObject(parsed) ? parsed : null;
		} catch (error) {
			console.warn(`Unable to parse the persisted ${label} draft`, error);
			return null;
		}
	}

	function clearDraft(): void {
		try {
			window.sessionStorage.removeItem(storageKey());
		} catch (error) {
			console.warn(`Unable to clear the persisted ${label} draft`, error);
		}
	}

	// A returned initialInstructionId (freshly created in the editor) takes precedence over the
	// draft's now-stale instructionId. When neither a returned id nor a valid draft id (string or
	// explicit null) is present, the current value is preserved — restore must not clobber an
	// instructionId the caller already holds with a null just because the draft omitted the field.
	function resolveInstructionId(
		draftInstructionId: unknown,
		initialInstructionId: string | null,
		currentInstructionId: string | null = null
	): string | null {
		if (initialInstructionId) {
			return initialInstructionId;
		}
		if (typeof draftInstructionId === "string" || draftInstructionId === null) {
			return draftInstructionId;
		}
		return currentInstructionId;
	}

	return { consumeReturnQuery, storageKey, returnedInstructionId, persistDraft, loadRawDraft, clearDraft, resolveInstructionId };
}
